// The post body schema: every block and inline construct in content/SYNTAX.md,
// and nothing else. Node views (the previews) are added in views.jsx; this file
// has no DOM code, so Node can load it to validate content-json files.
import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Blockquote from '@tiptap/extension-blockquote';
import HardBreak from '@tiptap/extension-hard-break';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import { BulletList, OrderedList, ListItem } from '@tiptap/extension-list';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';

const attr = (name, def = null) => ({
  [name]: {
    default: def,
    parseHTML: (el) => el.getAttribute('data-' + name) ?? def,
    renderHTML: (a) => (a[name] == null ? {} : { ['data-' + name]: a[name] }),
  },
});

// A node rendered as <tag data-type="name">, with the given content expression.
const box = (name, tag, spec) =>
  Node.create({
    name,
    group: 'block',
    defining: true,
    ...spec,
    parseHTML: () => [{ tag: `${tag}[data-type="${name}"]` }],
    renderHTML: ({ HTMLAttributes }) => [
      tag,
      mergeAttributes(HTMLAttributes, { 'data-type': name, class: 'b-' + name }),
      ...(spec.atom ? [] : [0]),
    ],
  });

// ## Text {#id}
const HeadingWithId = Heading.extend({
  addAttributes() {
    return { ...this.parent?.(), id: { default: null } };
  },
}).configure({ levels: [2, 3, 4, 5] });

// > quote … — cite: the citation is a node of its own, last in the quote.
const Quote = Blockquote.extend({ content: 'block+ cite?' });
const Cite = box('cite', 'p', { group: '', content: 'inline*' });

// Image, with size, alignment and an optional link around it.
const Image = box('image', 'div', {
  atom: true,
  draggable: true,
  addAttributes: () => ({
    ...attr('src'),
    ...attr('alt', ''),
    ...attr('width'),
    ...attr('height'),
    ...attr('align'),
    ...attr('href'),
  }),
});

// :::figure — one media block, then the caption.
const Figure = box('figure', 'figure', { content: '(image|table|embed|video) caption' });
const Caption = box('caption', 'figcaption', { group: '', content: 'inline*' });

// :::details[summary]{schema open title_tag}
const Details = box('details', 'div', {
  content: 'summary block+',
  addAttributes: () => ({ ...attr('schema'), ...attr('open', false), ...attr('titleTag') }),
});
const Summary = box('summary', 'div', { group: '', content: 'inline*' });

// ::embed{url provider type} — an oEmbed player (all YouTube today).
const Embed = box('embed', 'div', {
  atom: true,
  addAttributes: () => ({ ...attr('url'), ...attr('provider'), ...attr('type') }),
});

// :::embed{provider="twitter"} — a tweet, its text kept as the fallback content.
const Tweet = box('tweet', 'div', { content: 'block+', addAttributes: () => attr('url') });

// ::video{src}
const Video = box('video', 'div', { atom: true, addAttributes: () => attr('src') });

// :::gallery{columns} — images or captioned figures, then an optional caption.
const Gallery = box('gallery', 'div', {
  content: '(image|figure)+ caption?',
  addAttributes: () => ({
    columns: {
      default: null,
      parseHTML: (el) => el.getAttribute('data-columns'),
      renderHTML: (a) => (a.columns ? { 'data-columns': a.columns, style: `--columns:${a.columns}` } : {}),
    },
  }),
});

// $$ … $$
const MathBlock = box('mathBlock', 'div', { atom: true, addAttributes: () => attr('tex', '') });

// ```{=html} — copied byte for byte.
const RawHtml = box('rawHtml', 'div', { atom: true, addAttributes: () => attr('html', '') });

// $ … $
const MathInline = Node.create({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes: () => attr('tex', ''),
  parseHTML: () => [{ tag: 'span[data-type="mathInline"]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'mathInline' })],
});

// [^n] — the note text is the node's content; numbers come from document order.
const Footnote = Node.create({
  name: 'footnote',
  group: 'inline',
  inline: true,
  atom: true,
  content: '(text|mathInline|hardBreak)*',
  parseHTML: () => [{ tag: 'sup[data-type="footnote"]' }],
  renderHTML: () => ['sup', { 'data-type': 'footnote', class: 'b-footnote' }, 0],
});

export const extensions = [
  Document,
  Text,
  Paragraph,
  HeadingWithId,
  Quote,
  Cite,
  BulletList,
  OrderedList,
  ListItem,
  Table.configure({ resizable: false }).extend({
    // GFM: only the first row can be a header row, and it is all headers or none.
    addProseMirrorPlugins() {
      return [
        ...(this.parent?.() || []),
        new Plugin({
          appendTransaction(trs, _old, state) {
            if (!trs.some((tr) => tr.docChanged)) return null;
            const { tableHeader, tableCell } = state.schema.nodes;
            const fixes = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name !== 'table') return true;
              node.forEach((row, rowOff, r) => {
                const header = r === 0 && row.firstChild?.type === tableHeader;
                row.forEach((cell, cellOff) => {
                  const want = header ? tableHeader : tableCell;
                  if (cell.type !== want) fixes.push([pos + 1 + rowOff + 1 + cellOff, want, cell.attrs]);
                });
              });
              return false;
            });
            if (!fixes.length) return null;
            const tr = state.tr;
            for (const [at, type, attrs] of fixes) tr.setNodeMarkup(at, type, attrs);
            return tr;
          },
        }),
      ];
    },
  }),
  TableRow,
  // GFM cells: one paragraph each (line breaks are hard breaks)
  TableHeader.extend({ content: 'paragraph' }),
  TableCell.extend({ content: 'paragraph' }),
  Image,
  Figure,
  Caption,
  Details,
  Summary,
  Embed,
  Tweet,
  Video,
  Gallery,
  MathBlock,
  RawHtml,
  HardBreak,
  MathInline,
  Footnote,
  Bold,
  Italic,
  Link.configure({
    openOnClick: false,
    autolink: false,
    linkOnPaste: false,
    HTMLAttributes: { target: null, rel: null, class: null },
  }),
  Superscript,
  Subscript,
];

// Drop attributes that equal their default, so saved JSON carries only what was set.
function strip(item, type) {
  const out = { ...item };
  const attrs = Object.fromEntries(
    Object.entries(out.attrs || {}).filter(([k, v]) => v !== (type.spec.attrs?.[k]?.default ?? null))
  );
  if (Object.keys(attrs).length) out.attrs = attrs;
  else delete out.attrs;
  return out;
}

export function clean(json, schema) {
  const out = strip(json, schema.nodes[json.type]);
  if (out.marks) out.marks = out.marks.map((m) => strip(m, schema.marks[m.type]));
  if (out.content) out.content = out.content.map((c) => clean(c, schema));
  return out;
}
